import { Factory } from "@/data/dao/Factory";
import { VoucherDao } from "@/data/dao/service/VoucherDao";
import { VoucherState } from "@/data/model/Enums";
import type { IVoucher } from "@/data/model/Voucher";
import { DateTime } from "@/data/util/DateTime";

import { i18n } from "@/portal/Trans";
import type { IDataTableConfig } from "@/portal/elements/DataTable";
import { DataTable } from "@/portal/elements/DataTable";
import { portalEvents } from "@/portal/events/portalEvents";
import { VoucherBulkWindow } from "@/portal/modal/VoucherBulkWindow";
import { TastierGlobal } from "@/portal/globalAccess";

export class VoucherTableController {
    private $element: JQuery;
    
    private readonly locationId: string;
    private readonly _dao: VoucherDao;
    private dataTable: DataTable<IVoucher>;
    private bulkWindow?: VoucherBulkWindow;

    constructor(element: JQuery) {
        this.$element = element;

        this._dao = Factory.getInstance<VoucherDao>(VoucherDao);
        this.locationId = TastierGlobal.getLocationSwitch()?.getCurrentLocationId()!;

        const tableConfig: IDataTableConfig<IVoucher> = {
            columnMapping: [
                {
                    name: i18n("voucher.fields.created"),
                    property: "createdDateTime",
                    styles: { width: "14%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("voucher.fields.expiryDate"),
                    property: "expirationDateTime",
                    styles: { width: "14%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("voucher.fields.initialValue"),
                    property: "initialValue",
                    styles: { width: "10%" },
                    formatter: (m, p) => `CHF ${p.toFixed(2)}`,
                },
                {
                    name: i18n("voucher.fields.balance"),
                    property: "balance",
                    styles: { width: "10%" },
                    formatter: (m, p) => `CHF ${p.toFixed(2)}`,
                },
                {
                    name: i18n("voucher.fields.code"),
                    property: "code",
                    styles: { width: "17%" },
                },
                {
                    name: i18n("voucher.fields.internalNote"),
                    property: "internalNote",
                    styles: { width: "20%" },
                },
                {
                    name: i18n("general.fields.state"),
                    property: "state",
                    styles: { width: "7%" },
                    formatter: (m, p) => {
                        if (p === false) return "";

                        let styleClass = "kt-badge--primary";

                        switch (m.state) {
                            case VoucherState.Redeemed:
                                styleClass = "kt-badge--dark";
                                break;
                            case VoucherState.Enabled:
                                styleClass = "kt-badge--success";
                                break;
                            case VoucherState.Expired:
                            case VoucherState.Cancelled:
                                styleClass = "kt-badge--danger";
                                break;
                        }

                        return (
                            '<div class="kt-badge ' + styleClass + ' kt-badge--inline">' +
                            i18n("enums.VoucherState." + VoucherState[m.state]) +
                            "</div>"
                        );
                    },
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: (m) => {
                        if (m.locationId !== this.locationId) 
                            return "";
                        
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            locationDependend: true,
            defaultSortAsc: false,
            defaultSortProperty: "createdDateTime",
        };

        this.dataTable = new DataTable<IVoucher>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this._dao,
            tableConfig
        );

        this.dataTable.onClick(
            (m) => {
                if (m.locationId !== this.locationId)
                    return;
                
                location.href = "/voucher/" + m.id;
            },
            (m) => {
                if (m.locationId !== this.locationId)
                    return;
                
                window.open("/voucher/" + m.id);
            }
        );

        portalEvents.OnLocationChanged.add(async () => {
            this.dataTable.loadData(true);

            if (this.bulkWindow !== undefined) this.bulkWindow.needsTableReload = true;
        }, this);

        this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.bulk").click((e) => {
            e.preventDefault();
            this.onBulkEdit();
        });
    }

    private onBulkEdit() {
        if (this.bulkWindow === undefined) {
            this.bulkWindow = new VoucherBulkWindow();
            this.bulkWindow.onExecuted = () => this.dataTable.loadData(true);
        }

        this.bulkWindow.show();
    }
}
